import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from '@mui/material';

import { DataTable, type TableColumn, EditIcon, TrashIcon, PreviewIcon } from '@/src/ui';
import { ReportHeaderStyle, ReportCellStyle } from '@/src/utils';
import type { QuestionDTO } from '../models/questionDTOs';

interface QuestionsTableProps {
    data: Array<QuestionDTO>;
    showDeleteModal: boolean;
    setShowDeleteModal: (val: boolean) => void;
    setShowPreviewModal: (val: boolean) => void;
    setSelectedQuestion: (val: QuestionDTO | null) => void;
    searchString: string;
}

export default function QuestionsTable(props: QuestionsTableProps) {
    const {
        data,
        showDeleteModal,
        setShowPreviewModal,
        setShowDeleteModal,
        setSelectedQuestion,
        searchString,
    } = props;

    const filteredData = data.filter((question) => {
        const search = searchString.trim().toLowerCase();
        if (!search) return true;
        return question.title.trim().toLowerCase().includes(search);
    });

    const columns = useMemo<TableColumn[]>(() => {
        return [
            {
                dataKey: 'id',
                header: 'ID',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'title',
                header: 'Title',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'questionTypeMaster',
                header: 'Question Type',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'questionGrades',
                header: 'Question Grades',
                align: 'left',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: QuestionDTO) => {
                    return (
                        row.questionGrades.map((grade) => grade.gradeLevelName).join(', ') ||
                        '<No Grades>'
                    );
                },
            },
            {
                dataKey: 'questionStandards',
                header: 'Question Standards',
                align: 'left',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: QuestionDTO) => {
                    return (
                        row.questionStandards.map((standard) => standard.standardName).join(', ') ||
                        '<No Standards>'
                    );
                },
            },
            {
                dataKey: 'meta',
                header: 'PC/PT/DoK',
                align: 'left',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: QuestionDTO) => {
                    return (
                        <div>
                            {row.primaryClaim} / {row.primaryTarget} / {row.depthOfKnowledge}
                        </div>
                    );
                },
            },
            {
                dataKey: 'actions',
                header: 'Actions',
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: QuestionDTO) => {
                    return (
                        <>
                            <Tooltip title="Edit" arrow>
                                <Link
                                    to={`/questions/createEditQuestion?viewMode=edit&resourceId=${row.id}&questionTypeMasterId=${row.questionTypeMasterId}`}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0.6em 1.2em',
                                    }}
                                >
                                    <span role="img" aria-label="edit">
                                        <EditIcon />
                                    </span>
                                </Link>
                            </Tooltip>
                            <Tooltip title="Preview" arrow>
                                <button
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => {
                                        setSelectedQuestion(row);
                                        setShowPreviewModal(true);
                                    }}
                                >
                                    <span role="img" aria-label="preview">
                                        <PreviewIcon />
                                    </span>
                                </button>
                            </Tooltip>
                            <Tooltip title="Delete" arrow>
                                <button
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => {
                                        if (!showDeleteModal) {
                                            setSelectedQuestion(row);
                                            setShowDeleteModal(true);
                                        }
                                    }}
                                >
                                    <span role="img" aria-label="delete">
                                        <TrashIcon />
                                    </span>
                                </button>
                            </Tooltip>
                        </>
                    );
                },
            },
        ];
    }, [showDeleteModal, setShowDeleteModal, setSelectedQuestion, setShowPreviewModal]);

    return (
        <DataTable
            data={filteredData}
            columns={columns}
            tableName="Users"
            alternatingRowColor
            defaultSortColumn="title"
            defaultSortDirection="asc"
        />
    );
}
